export type HelixWidget =
  | 'injected'
  | 'pagination'
  | 'user'
  | 'game'
  | 'duration'
  | 'enum'
  | 'switch'
  | 'textarea'
  | 'datetime'
  | 'tokens'
  | 'repeatable'
  | 'input';

export interface HelixFormField {
  name: string;
  type: string;
  required?: boolean;
  isArray?: boolean;
  widget?: HelixWidget | string;
  hint?: string;
  enum?: string[];
  children?: HelixFormField[];
  exclusiveWith?: string[];
  minimum?: number;
  maximum?: number;
}

const INJECTED = new Set(['broadcaster_id', 'moderator_id', 'sender_id', 'from_broadcaster_id']);
const PAGINATION = new Set(['first', 'after', 'before']);
const USERS = new Set(['user_id', 'target_user_id', 'to_broadcaster_id', 'parent_id', 'from_id', 'to_id']);
const GAMES = new Set(['game_id', 'category_id']);
const TEXT = new Set(['reason', 'message', 'title', 'prompt', 'text']);

export function resolveWidget(field: HelixFormField): HelixWidget {
  if (field.widget) return field.widget as HelixWidget;
  const name = field.name;
  if (INJECTED.has(name)) return 'injected';
  if (PAGINATION.has(name)) return 'pagination';
  if (USERS.has(name)) return 'user';
  if (GAMES.has(name)) return 'game';
  if (name === 'duration') return 'duration';
  if (field.enum?.length) return 'enum';
  if (field.type === 'boolean') return 'switch';
  if (TEXT.has(name)) return 'textarea';
  if (field.isArray && field.children?.length) return 'repeatable';
  if (field.isArray) return 'tokens';
  if (name.endsWith('_at') || field.type === 'datetime') return 'datetime';
  return 'input';
}

export function annotateFields(fields: HelixFormField[]): HelixFormField[] {
  return fields.map((field) => ({
    ...field,
    widget: resolveWidget(field),
    children: field.children ? annotateFields(field.children) : undefined,
  }));
}

export function visibleFields(fields: HelixFormField[]): HelixFormField[] {
  return annotateFields(fields).filter((f) => f.widget !== 'injected' && f.widget !== 'pagination');
}

export function injectedFields(fields: HelixFormField[]): HelixFormField[] {
  return annotateFields(fields).filter((f) => f.widget === 'injected');
}

export function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

export function serializeHelixParams(
  fields: HelixFormField[],
  values: Record<string, unknown>,
  options: { permanentBan?: boolean } = {},
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of annotateFields(fields)) {
    if (field.widget === 'injected' || field.widget === 'pagination') continue;
    if (field.widget === 'duration' && options.permanentBan) continue;
    const value = values[field.name];
    if (isEmpty(value)) continue;
    if ((value === 0 || value === '0') && field.name === 'id' && !field.required) continue;
    if (field.children?.length && isPlainObject(value)) {
      const nested = serializeHelixParams(field.children, value as Record<string, unknown>, options);
      if (Object.keys(nested).length || field.required) out[field.name] = nested;
      continue;
    }
    if (field.isArray && Array.isArray(value)) {
      const items = value.filter((item) => !isEmpty(item));
      if (items.length) out[field.name] = items;
      continue;
    }
    if (field.widget === 'datetime' && typeof value === 'string') {
      out[field.name] = toRfc3339(value);
      continue;
    }
    out[field.name] = value;
  }
  return out;
}

export function serializeQueryRepeat(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (isEmpty(value)) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (isEmpty(item)) continue;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      }
      continue;
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.join('&');
}

export function toRfc3339(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    return new Date(`${value}:00`).toISOString();
  }
  return value;
}

export function getPath(values: Record<string, unknown>, path: string[]): unknown {
  let cursor: unknown = values;
  for (const key of path) {
    if (!isPlainObject(cursor)) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

export function setPath(values: Record<string, unknown>, path: string[], next: unknown): Record<string, unknown> {
  if (path.length === 0) return values;
  const [head, ...rest] = path;
  const copy = { ...values };
  if (rest.length === 0) {
    if (isEmpty(next)) delete copy[head!];
    else copy[head!] = next;
    return copy;
  }
  const child = isPlainObject(copy[head!]) ? (copy[head!] as Record<string, unknown>) : {};
  copy[head!] = setPath(child, rest, next);
  return copy;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export const DURATION_PRESETS = [
  { label: '1 minute', seconds: 60 },
  { label: '10 minutes', seconds: 600 },
  { label: '1 heure', seconds: 3600 },
  { label: '24 heures', seconds: 86400 },
  { label: '1 semaine', seconds: 604800 },
  { label: '2 semaines', seconds: 1209600 },
] as const;
